/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ScoreCardEvaluatorsService {

    /**
     * List the evaluators assigned to this scorecard instance
     * @param templateId
     * @param applicantId applicant external id
     * @returns any result holders, invited evaluators, and the derived default owner
     * @throws ApiError
     */
    public static getInternalApiScoreCardsTemplatesApplicantsEvaluators(
        templateId: number,
        applicantId: string,
    ): CancelablePromise<{
        applicant?: {
            /**
             * the applicant external id
             */
            id?: string;
            name?: string;
        };
        /**
         * whether the caller may invite and remove — the mutations require applicant management
         */
        can_manage?: boolean;
        /**
         * result holders in assignment order, then invited evaluators, then the derived default owner
         */
        evaluators?: Array<{
            user?: {
                /**
                 * the evaluator user external id
                 */
                id?: string | null;
                name?: string | null;
            };
            /**
             * pending covers an invited evaluator and the derived default owner
             */
            status?: 'submitted' | 'draft' | 'pending';
            submitted_at?: string | null;
            /**
             * the stored invitation when one exists, otherwise how the scorecard reached the applicant
             */
            invited_at?: string | null;
            /**
             * true on the derived default owner wherever they appear — removal cannot take them off the list, so nothing offers it
             */
            default?: boolean;
            /**
             * whether Send reminder is offered: the reminder rides on a stored invitation, and a submitted scorecard refuses it
             */
            remindable?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/evaluators',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
            },
            errors: {
                403: `the score-card-templates flag is off for this account`,
                404: `not found`,
            },
        });
    }

    /**
     * Invite users to evaluate this scorecard instance
     * @param templateId
     * @param applicantId applicant external id
     * @param requestBody
     * @returns any the invitations created, and the assigned list after them
     * @throws ApiError
     */
    public static postInternalApiScoreCardsTemplatesApplicantsEvaluators(
        templateId: number,
        applicantId: string,
        requestBody: {
            /**
             * user external ids; a user already invited keeps their original invitation
             */
            user_ids: Array<string>;
        },
    ): CancelablePromise<{
        /**
         * how many of the users were not already invited
         */
        invited_count?: number;
        evaluators?: Array<{
            user?: {
                /**
                 * the evaluator user external id
                 */
                id?: string | null;
                name?: string | null;
            };
            /**
             * pending covers an invited evaluator and the derived default owner
             */
            status?: 'submitted' | 'draft' | 'pending';
            submitted_at?: string | null;
            /**
             * the stored invitation when one exists, otherwise how the scorecard reached the applicant
             */
            invited_at?: string | null;
            /**
             * true on the derived default owner wherever they appear — removal cannot take them off the list, so nothing offers it
             */
            default?: boolean;
            /**
             * whether Send reminder is offered: the reminder rides on a stored invitation, and a submitted scorecard refuses it
             */
            remindable?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/evaluators',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `the user cannot manage the applicant`,
                404: `a user id does not belong to this account`,
                422: `user_ids is missing`,
            },
        });
    }

    /**
     * Remove an evaluator from this scorecard instance
     * @param templateId
     * @param applicantId applicant external id
     * @param userId the evaluator user external id
     * @returns any the invitation and any unsubmitted draft are gone, and the assigned list follows
     * @throws ApiError
     */
    public static deleteInternalApiScoreCardsTemplatesApplicantsEvaluators(
        templateId: number,
        applicantId: string,
        userId: string,
    ): CancelablePromise<{
        applicant?: {
            /**
             * the applicant external id
             */
            id?: string;
            name?: string;
        };
        /**
         * whether the caller may invite and remove — the mutations require applicant management
         */
        can_manage?: boolean;
        /**
         * result holders in assignment order, then invited evaluators, then the derived default owner
         */
        evaluators?: Array<{
            user?: {
                /**
                 * the evaluator user external id
                 */
                id?: string | null;
                name?: string | null;
            };
            /**
             * pending covers an invited evaluator and the derived default owner
             */
            status?: 'submitted' | 'draft' | 'pending';
            submitted_at?: string | null;
            /**
             * the stored invitation when one exists, otherwise how the scorecard reached the applicant
             */
            invited_at?: string | null;
            /**
             * true on the derived default owner wherever they appear — removal cannot take them off the list, so nothing offers it
             */
            default?: boolean;
            /**
             * whether Send reminder is offered: the reminder rides on a stored invitation, and a submitted scorecard refuses it
             */
            remindable?: boolean;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/evaluators/{user_id}',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
                'user_id': userId,
            },
            errors: {
                403: `the user cannot manage the applicant`,
                404: `the user id does not belong to this account`,
                422: `the evaluator already submitted — submission is one-way, so removal cannot erase a score`,
            },
        });
    }

    /**
     * Email an invited evaluator a reminder to complete their scorecard
     * @param templateId
     * @param applicantId applicant external id
     * @param userId the evaluator user external id
     * @returns void
     * @throws ApiError
     */
    public static postInternalApiScoreCardsTemplatesApplicantsEvaluatorsReminder(
        templateId: number,
        applicantId: string,
        userId: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/evaluators/{user_id}/reminder',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
                'user_id': userId,
            },
            errors: {
                403: `the user cannot manage the applicant`,
                404: `the evaluator holds no stored invitation — the reminder rides on the original request`,
                422: `the evaluator already submitted — there is nothing left to remind them of`,
            },
        });
    }

    /**
     * Search account users to invite as evaluators
     * @param templateId
     * @param applicantId applicant external id
     * @param query matches user name or email; under three characters returns nothing
     * @returns any matching users not yet assigned to this scorecard instance
     * @throws ApiError
     */
    public static getInternalApiScoreCardsTemplatesApplicantsEvaluatorsSearch(
        templateId: number,
        applicantId: string,
        query: string,
    ): CancelablePromise<{
        users?: Array<{
            /**
             * the user external id
             */
            id?: string;
            name?: string | null;
            email?: string;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/score_cards/templates/{template_id}/applicants/{applicant_id}/evaluators/search',
            path: {
                'template_id': templateId,
                'applicant_id': applicantId,
            },
            query: {
                'query': query,
            },
        });
    }

}